import { useCallback, useEffect, useState } from 'react'
import { Button, Col, Row } from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'
import { apiRequest, ensureCompanyInfo, getUserAccess, showKAlert } from '../helpers'
import { t } from '../locale'
import { UserAccess } from '../react-app-env'
import { getPointsPackages } from '../service/stripeConfig'
import { formatPrice } from '../service/currency'

const PRICES = getPointsPackages()

export const CheckoutForm = () => {
  const [selectedPrice, setSelectedPrice] = useState('')
  const [selectedPoints, setSelectedPoints] = useState(0)
  const [subscription, setSubscription] = useState(false)
  const [stripeCustomer, setStripeCustomer] = useState('')
  const [trial, setTrial] = useState(false)
  const [points, setPoints] = useState(0)
  const [days, setDays] = useState(0)
  const accessToken: any = sessionStorage.getItem('accessToken')
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null)

  const userAccess: UserAccess = getUserAccess()

  useEffect(() => {
    apiRequest(`${process.env.REACT_APP_API_URL}/subscription/check`, 'POST', {
      status: 'active',
    }).then(response => {
      setStripeCustomer(response.customerId)
      if (response.status && response.type === 'subscription') {
        setSubscription(true)
      } else if (
        response.status &&
        response.type === 'trial' &&
        (response.pointsLeft <= 1 || response.daysLeft <= 0)
      ) {
        setSubscription(true)
        setTrial(false)
      } else if (response.status && response.type === 'trial') {
        setTrial(true)
        setPoints(response.pointsLeft)
        setDays(response.daysLeft)
      }
    })
  }, [subscription])

  const startCheckout = useCallback(async () => {
    try {
      const url = subscription
        ? `${process.env.REACT_APP_API_URL}/stripe/subscription/create-checkout-session`
        : `${process.env.REACT_APP_API_URL}/stripe/create-checkout-session`

      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          accessToken,
          companyInfo: await ensureCompanyInfo(),
          priceId: selectedPrice,
          points: selectedPoints,
          customer: stripeCustomer,
        }),
      })

      if (!res.ok) throw new Error(`Checkout session request failed: ${res.status}`)

      const data = await res.json()
      if (typeof data.url !== 'string' || !data.url) {
        throw new Error('Checkout session response is missing url')
      }
      window.location.assign(data.url)
    } catch (err) {
      console.error('Failed to start checkout: ', err)
      showKAlert('danger', t('requestProcessingError'))
    }
  }, [selectedPrice, accessToken, stripeCustomer, subscription, selectedPoints])

  return (
    <>
      {trial && (
        <div className='alert alert-info d-flex align-items-start' role='alert'>
          <div className='me-2'>
            <i className='bi bi-lightning-charge-fill text-primary fs-4'></i>
          </div>
          <div>
            <h5 className='alert-heading mb-1'>{t('trialPeriodActive')}</h5>
            <p className='mb-0'>
              {t('trialMode')}
              <br />
              <strong>{days}</strong> {days !== 1 ? t('days') : t('day')} {t('remaining')}
              <strong> {points}</strong> {points !== 1 ? t('points') : t('point')} {t('left')}.
            </p>
            <br />
            <p className='mb-0 mt-2 text-muted'>
              <i className='bi bi-info-circle me-1'></i>
              {t('trialExpiryInfo')}
            </p>
            <p className='mb-0 mt-2'>{t('endTrialSubscribeInfo')}</p>
            <button
              className='btn btn-outline-primary btn-sm mt-3'
              onClick={() => window.location.assign('/user/billing')}
            >
              {t('Subscribe')}
            </button>
          </div>
        </div>
      )}
      {!trial && subscription && (
        <div className='alert alert-info d-flex align-items-start' role='alert'>
          <div className='me-4'>
            <i className='bi bi-lightning-charge-fill text-primary fs-4'></i>
          </div>
          <div>
            <h5 className='alert-heading mb-1'>{t('freeTrialEnded')}</h5>
            <p className='mb-0 mt-2'>{t('pleaseSubscribe')}</p>
            <button
              className='btn-primary mt-3 me-4 subscribe-button'
              onClick={() => window.location.assign('/user/billing')}
              data-label={t('Subscribe')}
            >
              <p data-label={t('Subscribe')}>{t('Subscribe')}</p>
              <svg
                xmlns='http://www.w3.org/2000/svg'
                className='h-6 w-6'
                fill='none'
                viewBox='0 0 24 24'
                stroke='currentColor'
                stroke-width='4'
              >
                <path
                  stroke-linecap='round'
                  stroke-linejoin='round'
                  d='M14 5l7 7m0 0l-7 7m7-7H3'
                ></path>
              </svg>
            </button>
          </div>
          <div></div>
        </div>
      )}
      {!trial && !subscription && userAccess.permissions.main && (
        <>
          <Row className='border-0 justify-content-center'>
            {PRICES.map((price, index) => (
              <div key={price.id} className='col-md-4 mb-3 billing-cards'>
                <div
                  onMouseEnter={() => setHoveredIndex(index)}
                  onMouseLeave={() => setHoveredIndex(null)}
                  className={`billing-card text-center priceCard
                    ${selectedPrice === price.id ? 'priceCardSelected' : ''}
                    ${hoveredIndex === index && selectedPrice !== price.id ? 'priceCardHover' : ''}
                    `}
                  onClick={() => {
                    if (price.id && price.points) {
                      setSelectedPrice(price.id)
                      setSelectedPoints(Number(price.points))
                    }
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  <p className='tip'>
                    {price.points} {t('points')}
                  </p>
                  <p className='second-text'>{formatPrice(price.amount ?? '')}</p>
                  <small className='text-muted'>{t('useThesePoints')}</small>
                </div>
              </div>
            ))}
          </Row>
          <Row className='justify-content-center mt-4'>
            <Col md={6} className='text-center'>
              <Button
                onClick={() => {
                  if (!selectedPrice) {
                    showKAlert('warning', 'selectPoints')
                    return
                  }
                  startCheckout()
                }}
                className='btn btn-primary px-4 py-2'
              >
                {t('proceedPayment')}
              </Button>
            </Col>
          </Row>
        </>
      )}
    </>
  )
}

export const Return = () => {
  const navigate = useNavigate()

  useEffect(() => {
    const queryString = window.location.search
    const urlParams = new URLSearchParams(queryString)
    const sessionId = urlParams.get('session_id')

    const btn = document.getElementById('btn-submit-company')

    fetch(`${process.env.REACT_APP_API_URL}/stripe/session-status?session_id=${sessionId}`)
      .then(res => res.json())
      .then(data => {
        btn?.classList.remove('disabled')

        localStorage.removeItem('subscriptionData')
        window.dispatchEvent(new Event('subscriptionUpdated'))

        if (data.initial) {
          navigate('/company')
        } else {
          navigate('/company')
        }
      })
  }, [navigate])

  return null
}
